import { useState, useEffect, useCallback, useReducer, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import dayjs from 'dayjs';

/**
 * Enhanced debounce hook with cleanup
 */
export const useDebounce = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Table state reducer for complex state management
 */
const tableStateReducer = (state, action) => {
  switch (action.type) {
    case 'SET_PAGE':
      return { ...state, page: action.payload };
    case 'SET_PER_PAGE':
      return { ...state, perPage: parseInt(action.payload, 10), page: 1 };
    case 'SET_SORT':
      return { ...state, sort: action.payload };
    case 'SET_SEARCH':
      return { ...state, search: action.payload, page: 1 };
    case 'SET_FILTERS':
      return { ...state, filters: action.payload, page: 1 };
    case 'UPDATE_FILTER':
      return {
        ...state,
        filters: { ...state.filters, [action.key]: action.value },
        page: 1
      };
    case 'RESET_FILTERS':
      return { ...state, filters: {}, page: 1 };
    case 'RESET_STATE':
      return action.payload;
    default:
      return state;
  }
};

/**
 * Enhanced table state management hook
 */
export const useAdvancedTable = ({
  defaultPage = 1,
  defaultPerPage = 10,
  defaultSort = null,
  defaultFilters = {},
  defaultSearch = '',
  tableId = 'default',
  persistState = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize state from URL or localStorage
  const getInitialState = useCallback(() => {
    let initialState = {
      page: defaultPage,
      perPage: defaultPerPage,
      sort: defaultSort,
      filters: defaultFilters,
      search: defaultSearch,
    };

    // Load from URL if persistState is enabled
    if (persistState) {
      const params = queryString.parse(location.search);

      if (params.page) initialState.page = parseInt(params.page, 10) || defaultPage;
      if (params.perPage) initialState.perPage = parseInt(params.perPage, 10) || defaultPerPage;
      if (params.search) initialState.search = params.search;

      if (params.sortField && params.sortOrder) {
        initialState.sort = { field: params.sortField, order: params.sortOrder };
      }

      // Parse filters from URL
      const urlFilters = {};
      Object.keys(params).forEach(key => {
        if (key.endsWith('_start') || key.endsWith('_end')) {
          const filterKey = key.replace('_start', '').replace('_end', '');
          if (!urlFilters[filterKey]) urlFilters[filterKey] = {};
          urlFilters[filterKey][key.endsWith('_start') ? 'start' : 'end'] = dayjs(params[key], 'DD-MM-YYYY').toDate();
        } else if (!['page', 'perPage', 'search', 'sortField', 'sortOrder'].includes(key)) {
          urlFilters[key] = params[key].includes(',') ? params[key].split(',') : params[key];
        }
      });

      initialState.filters = { ...defaultFilters, ...urlFilters };
    }

    return initialState;
  }, [defaultPage, defaultPerPage, defaultSort, defaultFilters, defaultSearch, location.search, persistState]);

  const [state, dispatch] = useReducer(tableStateReducer, null, getInitialState);

  // Debounced search
  const debouncedSearch = useDebounce(state.search, 500);

  // Sync state to URL when persistState is enabled
  useEffect(() => {
    if (!persistState) return;

    const params = {
      page: state.page,
      perPage: state.perPage,
      ...(state.sort && { sortField: state.sort.field, sortOrder: state.sort.order }),
      ...(debouncedSearch && { search: debouncedSearch }),
    };

    // Add filters to params
    Object.entries(state.filters).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;

      if (Array.isArray(value)) {
        params[key] = value.join(',');
      } else if (typeof value === 'object' && value.start && value.end) {
        params[`${key}_start`] = dayjs(value.start).format('DD-MM-YYYY');
        params[`${key}_end`] = dayjs(value.end).format('DD-MM-YYYY');
      } else {
        params[key] = value;
      }
    });

    // Remove empty values
    Object.keys(params).forEach(key => {
      if (!params[key] || params[key] === defaultPage ||
        (key === 'perPage' && params[key] === defaultPerPage)) {
        delete params[key];
      }
    });

    const search = queryString.stringify(params);
    if (location.search !== `?${search}`) {
      navigate({ search }, { replace: true });
    }
  }, [state.page, state.perPage, state.sort, debouncedSearch, state.filters,
    persistState, navigate, location.search, defaultPage, defaultPerPage]);

  // Action creators
  const setPage = useCallback((page) => {
    dispatch({ type: 'SET_PAGE', payload: page });
  }, []);

  const setPerPage = useCallback((perPage) => {
    dispatch({ type: 'SET_PER_PAGE', payload: perPage });
  }, []);

  const setSort = useCallback((sort) => {
    dispatch({ type: 'SET_SORT', payload: sort });
  }, []);

  const setSearch = useCallback((search) => {
    dispatch({ type: 'SET_SEARCH', payload: search });
  }, []);

  const setFilters = useCallback((filters) => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  }, []);

  const updateFilter = useCallback((key, value) => {
    dispatch({ type: 'UPDATE_FILTER', key, value });
  }, []);

  const resetFilters = useCallback(() => {
    dispatch({ type: 'RESET_FILTERS' });
  }, []);

  const resetState = useCallback(() => {
    dispatch({
      type: 'RESET_STATE',
      payload: {
        page: defaultPage,
        perPage: defaultPerPage,
        sort: defaultSort,
        filters: defaultFilters,
        search: defaultSearch,
      }
    });
  }, [defaultPage, defaultPerPage, defaultSort, defaultFilters, defaultSearch]);

  // Computed values
  const activeFilterCount = useMemo(() => {
    return Object.values(state.filters).filter(v =>
      v !== null && v !== undefined && v !== '' &&
      !(Array.isArray(v) && v.length === 0)
    ).length;
  }, [state.filters]);

  const hasActiveFilters = useMemo(() => activeFilterCount > 0, [activeFilterCount]);

  return {
    // State
    page: state.page,
    perPage: state.perPage,
    sort: state.sort,
    search: state.search,
    filters: state.filters,
    debouncedSearch,

    // Computed
    activeFilterCount,
    hasActiveFilters,

    // Actions
    setPage,
    setPerPage,
    setSort,
    setSearch,
    setFilters,
    updateFilter,
    resetFilters,
    resetState,
  };
};

/**
 * Row selection management hook
 */
export const useRowSelection = () => {
  const [selectedRows, setSelectedRows] = useState([]);

  const handleRowSelect = useCallback((selectedRowKeys, selectedRows) => {
    setSelectedRows(selectedRows);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedRows([]);
  }, []);

  const selectAll = useCallback((rows) => {
    setSelectedRows(rows);
  }, []);

  const toggleRow = useCallback((row) => {
    setSelectedRows(prev => {
      const isSelected = prev.some(r => r.id === row.id);
      return isSelected
        ? prev.filter(r => r.id !== row.id)
        : [...prev, row];
    });
  }, []);

  const isSelected = useCallback((row) => {
    return selectedRows.some(r => r.id === row.id);
  }, [selectedRows]);

  const selectedCount = useMemo(() => selectedRows.length, [selectedRows]);

  return {
    selectedRows,
    selectedCount,
    selectedRowKeys: selectedRows.map(row => row.id),
    handleRowSelect,
    clearSelection,
    selectAll,
    toggleRow,
    isSelected,
  };
};

/**
 * Column visibility management hook
 */
export const useColumnVisibility = (columns, tableId = 'default', persistState = false) => {
  const getInitialVisibility = useCallback(() => {
    const defaultVisible = columns.filter(col => !col.omit).map(col => col.id || col.name);

    if (!persistState) return defaultVisible;

    try {
      const saved = localStorage.getItem(`tableColumns_${tableId}`);
      return saved ? JSON.parse(saved) : defaultVisible;
    } catch (error) {
      console.error('Error loading column visibility:', error);
      return defaultVisible;
    }
  }, [columns, tableId, persistState]);

  const [visibleColumns, setVisibleColumns] = useState(getInitialVisibility);

  // Save to localStorage when state changes
  useEffect(() => {
    if (persistState) {
      try {
        localStorage.setItem(`tableColumns_${tableId}`, JSON.stringify(visibleColumns));
      } catch (error) {
        console.error('Error saving column visibility:', error);
      }
    }
  }, [visibleColumns, persistState, tableId]);

  const toggleColumn = useCallback((columnId) => {
    setVisibleColumns(prev =>
      prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const showAll = useCallback(() => {
    setVisibleColumns(columns.map(col => col.id || col.name));
  }, [columns]);

  const hideAll = useCallback(() => {
    setVisibleColumns([]);
  }, []);

  const resetToDefault = useCallback(() => {
    setVisibleColumns(columns.filter(col => !col.omit).map(col => col.id || col.name));
  }, [columns]);

  const getVisibleColumns = useCallback(() => {
    return columns.filter(col => visibleColumns.includes(col.id || col.name));
  }, [columns, visibleColumns]);

  return {
    visibleColumns,
    toggleColumn,
    showAll,
    hideAll,
    resetToDefault,
    getVisibleColumns,
  };
};